/**
 * Form Validation Hooks
 * 
 * This file contains custom hooks for common form validation patterns.
 * It implements the form validation strategy defined in docs/error-handling-strategy.md.
 */

import {useEffect, useState} from 'react';
import {
  validateEmail,
  validatePasswords,
  validateRequired,
  validateRequiredArray
} from './validation';

/**
 * Hook for validating a required field
 * @param value - The value to validate
 * @param errorMessage - Custom error message
 * @returns Validation state: [isValid, errorMessage, validate]
 */
export function useRequiredValidation(
  value: string,
  errorMessage = 'Це поле є обов\'язковим'
): [boolean, string, () => boolean] {
  const [isValid, setIsValid] = useState(true);
  const [error, setError] = useState('');

  const validate = () => {
    const valid = validateRequired(value);
    setIsValid(valid);
    setError(valid ? '' : errorMessage);
    return valid;
  };

  useEffect(() => {
    // Don't show error until user interacts with the field
    if (value !== '') {
      validate();
    }
  }, [value]);

  return [isValid, error, validate];
}

/**
 * Hook for validating an email field
 * @param email - The email to validate
 * @param required - Whether the field is required
 * @param errorMessage - Custom error message
 * @returns Validation state: [isValid, errorMessage, validate]
 */
export function useEmailValidation(
  email: string,
  required = false,
  errorMessage = 'Невірний формат email'
): [boolean, string, () => boolean] {
  const [isValid, setIsValid] = useState(true);
  const [error, setError] = useState('');

  const validate = () => {
    // If the field is empty and not required, it's valid
    if (!email && !required) {
      setIsValid(true);
      setError('');
      return true;
    }

    // If the field is empty but required, it's invalid
    if (!email && required) {
      setIsValid(false);
      setError('Це поле є обов\'язковим');
      return false;
    }

    // Validate email format
    const valid = validateEmail(email);
    setIsValid(valid);
    setError(valid ? '' : errorMessage);
    return valid;
  };

  useEffect(() => {
    // Don't show error until user interacts with the field
    if (email !== '') {
      validate();
    }
  }, [email]);

  return [isValid, error, validate];
}

/**
 * Hook for validating password confirmation
 * @param password - The password
 * @param confirmPassword - The confirmation password
 * @param errorMessage - Custom error message
 * @returns Validation state: [isValid, errorMessage, validate]
 */
export function usePasswordConfirmationValidation(
  password: string,
  confirmPassword: string,
  errorMessage = 'Паролі не співпадають'
): [boolean, string, () => boolean] {
  const [isValid, setIsValid] = useState(true);
  const [error, setError] = useState('');

  const validate = () => {
    // Only validate if both fields have values
    if (password && confirmPassword) {
      const valid = validatePasswords(password, confirmPassword);
      setIsValid(valid);
      setError(valid ? '' : errorMessage);
      return valid;
    }
    
    // If either field is empty, consider it valid for now
    setIsValid(true);
    setError('');
    return true;
  };

  useEffect(() => {
    // Don't show error until user interacts with both fields
    if (password !== '' && confirmPassword !== '') {
      validate();
    }
  }, [password, confirmPassword]);

  return [isValid, error, validate];
}

/**
 * Hook for validating an array of values (e.g., for multi-select)
 * @param values - The array of values
 * @param errorMessage - Custom error message
 * @returns Validation state: [isValid, errorMessage, validate]
 */
export function useArrayValidation(
  values: any[],
  errorMessage = 'Виберіть хоча б один елемент'
): [boolean, string, () => boolean] {
  const [isValid, setIsValid] = useState(true);
  const [error, setError] = useState('');

  const validate = () => {
    const valid = validateRequiredArray(values);
    setIsValid(valid);
    setError(valid ? '' : errorMessage);
    return valid;
  };

  useEffect(() => {
    validate();
  }, [values]);

  return [isValid, error, validate];
}

/**
 * Hook for validating a form with multiple fields
 * @param validators - Array of validation functions
 * @returns [isFormValid, validateForm]
 */
export function useFormValidation(validators: (() => boolean)[]): [boolean, () => boolean] {
  const [isFormValid, setIsFormValid] = useState(false);

  const validateForm = () => {
    // Run all validators and check if all return true
    const formValid = validators.every(validate => validate());
    setIsFormValid(formValid);
    return formValid;
  };

  return [isFormValid, validateForm];
}
